/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */

import { makeAutoObservable, runInAction } from 'mobx'

import PersistedCollection from '@src/service/collections/PersistedCollection'

import type Service from '.'
import { AttioIntegrationModel } from './model/integration/AttioIntegrationModel'
import { ClioIntegrationModel } from './model/integration/ClioIntegrationModel'
import { EmailIntegrationModel } from './model/integration/EmailIntegrationModel'
import { GongIntegrationModel } from './model/integration/GongIntegrationModel'
import { HighLevelIntegrationModel } from './model/integration/HighLevelIntegrationModel'
import { HousecallProIntegrationModel } from './model/integration/HousecallProIntegrationModel'
import { HubspotIntegrationModel } from './model/integration/HubspotIntegrationModel'
import { JobTreadIntegrationModel } from './model/integration/JobTreadIntegrationModel'
import { JobberIntegrationModel } from './model/integration/JobberIntegrationModel'
import { MondayIntegrationModel } from './model/integration/MondayIntegrationModel'
import { PipedriveIntegrationModel } from './model/integration/PipedriveIntegrationModel'
import { SalesforceIntegrationModel } from './model/integration/SalesforceIntegrationModel'
import { SlackIntegrationModel } from './model/integration/SlackIntegrationModel'
import { WebhookIntegrationModel } from './model/integration/WebhookIntegrationModel'
import { ZohoIntegrationModel } from './model/integration/ZohoIntegrationModel'
import type { Integration, LegacyIntegration } from './model/integration/types'
import type {
  CreateSlackAuthParams,
  IntegrationCreateApiKeyParams,
  IntegrationCreateClioParams,
  IntegrationCreateParams,
  IntegrationCreateZohoParams,
} from './transport/integration'
import type { IntegrationRepository } from './worker/repository'

export default class IntegrationStore {
  collection: PersistedCollection<Integration, IntegrationRepository>

  /** Whether integrations have been fetched from the API at least once. */
  fetched = false

  constructor(private service: Service) {
    this.collection = new PersistedCollection({
      table: service.storage.table('integration'),
      classConstructor: (json) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
        switch (json.type) {
          case 'attio':
            return new AttioIntegrationModel(service.integration)
          case 'email':
            return new EmailIntegrationModel(service.integration)
          case 'gong':
            return new GongIntegrationModel(service.integration)
          case 'hubspot':
            return new HubspotIntegrationModel(service.integration)
          case 'jobber':
            return new JobberIntegrationModel(service.integration)
          case 'pipedrive':
            return new PipedriveIntegrationModel(service.integration)
          case 'salesforce':
            return new SalesforceIntegrationModel(service.integration)
          case 'slack':
            return new SlackIntegrationModel(service.integration)
          case 'webhook':
            return new WebhookIntegrationModel()
          case 'zoho':
            return new ZohoIntegrationModel(service.integration)
          case 'clio':
          case 'clio-ca':
            return new ClioIntegrationModel(service.integration)
          case 'highlevel':
            return new HighLevelIntegrationModel(service.integration)
          case 'jobtread':
            return new JobTreadIntegrationModel(service.integration)
          case 'housecallpro':
            return new HousecallProIntegrationModel(service.integration)
          case 'monday':
            return new MondayIntegrationModel(service.integration)
          default:
            return null
        }
      },
    })
    makeAutoObservable(this)

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.load()
  }

  load() {
    return this.collection.performQuery((repo) => repo.all())
  }

  fetchAll() {
    return this.service.transport.integration.getAll().then((res) => {
      void this.collection.load(res, { deleteOthers: true })
      runInAction(() => {
        this.fetched = true
      })
    })
  }

  getApplication(clientId: string, redirectUrl: string) {
    return this.service.transport.account.application.get(clientId, redirectUrl)
  }

  fetchForPhoneNumber(phoneNumberId: string) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.collection.performQuery((repo) => repo.all())
    return this.service.transport.integration.legacy
      .get(phoneNumberId)
      .then(this.collection.load)
      .then((objs) =>
        /**
         * delete other locally saved integrations for this phone number that
         * are not returned from the API
         */
        this.collection.deleteQuery((repo) =>
          repo.othersForPhoneNumber(
            objs.map((o) => o.id),
            phoneNumberId,
          ),
        ),
      )
  }

  createAttio(params: IntegrationCreateParams) {
    return this.service.transport.integration.createAttio(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('attio')
      return this.collection.load(result)
    })
  }

  createGong(params: IntegrationCreateParams) {
    return this.service.transport.integration.createGong(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('gong')
      return this.collection.load(result)
    })
  }

  createHubspot(params: IntegrationCreateParams) {
    return this.service.transport.integration.createHubspot(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('hubspot')
      return this.collection.load(result)
    })
  }

  createJobber(params: IntegrationCreateParams) {
    return this.service.transport.integration.createJobber(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('jobber')
      return this.collection.load(result)
    })
  }

  createPipedrive(params: IntegrationCreateParams) {
    return this.service.transport.integration.createPipedrive(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('pipedrive')
      return this.collection.load(result)
    })
  }

  createSalesforce(params: IntegrationCreateParams) {
    return this.service.transport.integration.createSalesforce(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('salesforce')
      return this.collection.load(result)
    })
  }

  createSlack(params: CreateSlackAuthParams) {
    return this.service.transport.integration.legacy
      .createSlack(params)
      .then(this.collection.load)
  }

  createZoho(params: IntegrationCreateZohoParams) {
    return this.service.transport.integration.createZoho(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected('zoho')
      return this.collection.load(result)
    })
  }

  createClio(params: IntegrationCreateClioParams) {
    return this.service.transport.integration.createClio(params).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationConnected(params.type)
      return this.collection.load(result)
    })
  }

  async createHighLevel(params: IntegrationCreateParams) {
    const result = await this.service.transport.integration.createHighLevel(params)
    this.service.analytics.workspace.workspaceIntegrationConnected('highlevel')
    return this.collection.load(result)
  }

  async createJobTread(params: IntegrationCreateParams) {
    const result = await this.service.transport.integration.createJobTread(params)
    this.service.analytics.workspace.workspaceIntegrationConnected('jobtread')
    return this.collection.load(result)
  }

  async createMonday(params: IntegrationCreateParams) {
    const result = await this.service.transport.integration.createMonday(params)
    this.service.analytics.workspace.workspaceIntegrationConnected('monday')
    return this.collection.load(result)
  }

  async createHousecallPro(params: IntegrationCreateApiKeyParams) {
    const result = await this.service.transport.integration.createHousecallPro(params)
    this.service.analytics.workspace.workspaceIntegrationConnected('housecallpro')
    return this.collection.load(result)
  }

  createEmail(integration: Partial<LegacyIntegration>) {
    return this.service.transport.integration.legacy
      .createEmail(integration)
      .then(this.collection.load)
  }

  update(integration: Integration) {
    this.collection.put(integration)
    return this.service.transport.integration
      .update(integration.serialize())
      .then(this.collection.load)
  }

  updateLegacy(integration: LegacyIntegration) {
    this.collection.put(integration)
    return this.service.transport.integration.legacy
      .update(integration.serialize())
      .then(this.collection.load)
  }

  delete(integration: Integration) {
    this.collection.delete(integration)
    return this.service.transport.integration.delete(integration.id).then((result) => {
      this.service.analytics.workspace.workspaceIntegrationDisconnected(integration.type)
      return result
    })
  }

  deleteLegacy(integration: LegacyIntegration) {
    this.collection.delete(integration)
    return this.service.transport.integration.legacy.delete(integration.serialize())
  }

  getStatus(integration: Integration) {
    return this.service.transport.integration.status(integration.id)
  }

  isConnectedByName(integration: string) {
    return this.collection.list.some((i) => i.type === integration)
  }

  /**
   * Integration-related user properties for Amplitude.
   * Returns `undefined` until the initial fetchAll() call resolves,
   * then returns the connection status of each supported integration.
   */
  get analyticsProperties(): Record<string, unknown> | undefined {
    if (!this.fetched) {
      return undefined
    }
    return {
      hubspot_integration_enabled: this.isConnectedByName('hubspot'),
      attio_integration_enabled: this.isConnectedByName('attio'),
      gong_integration_enabled: this.isConnectedByName('gong'),
      jobber_integration_enabled: this.isConnectedByName('jobber'),
      pipedrive_integration_enabled: this.isConnectedByName('pipedrive'),
      salesforce_integration_enabled: this.isConnectedByName('salesforce'),
      slack_integration_enabled: this.isConnectedByName('slack'),
      email_integration_enabled: this.isConnectedByName('email'),
    }
  }

  deleteLocalByName(integration: string) {
    const integrationIds = this.collection.list
      .filter((i) => i.type === integration)
      .map((i) => i.id)

    this.collection.deleteBulk(integrationIds)
  }
}
